import type { ThemeSettings } from '@aquilixshop/ui-react';

/**
 * The builder canvas applies the theme's `--aqx-theme-font` / `--aqx-theme-heading-font` VARS
 * (BuilderCanvas `themeStyle`) but — unlike the storefront (which self-hosts the resolved faces) — never
 * LOADS the actual webfont. So a font-FAMILY change (e.g. Roboto → Poppins) is invisible in the canvas: the
 * family name resolves to a system fallback because the face isn't loaded. (Font WEIGHT/size changes still
 * show, since the system fallback honours them.) {@link themeFontLinkHref} builds a Google-Fonts css2 URL
 * for the theme's heading + body families so the canvas can load them and render the real font while editing.
 */

// Only true generic keywords + non-Google system faces: we only ever inspect the PRIMARY (first) family, so
// real Google families used as a primary (Poppins, Roboto, Inter, …) MUST NOT be here — they load fine.
const GENERIC = new Set([
  'system-ui',
  '-apple-system',
  'blinkmacsystemfont',
  'segoe ui',
  'helvetica',
  'helvetica neue',
  'arial',
  'sans-serif',
  'serif',
  'monospace',
  'inherit',
  'ui-sans-serif',
]);

/** First real family name from a CSS font stack (`"'Poppins', system-ui, …"` → `Poppins`); null if generic. */
export function primaryFamily(stack: string | undefined): string | null {
  if (typeof stack !== 'string') return null;
  const first = (stack.split(',')[0] ?? '').trim().replace(/^['"]|['"]$/g, '');
  if (!first || GENERIC.has(first.toLowerCase())) return null;
  // Reject anything that isn't a plain font name (defence against a CSS-breaking value reaching the URL).
  if (!/^[a-z0-9 ]+$/i.test(first)) return null;
  return first;
}

/**
 * Google-Fonts css2 URL loading the theme's heading + body families at the weights the canvas needs — the
 * role weight plus 400 (regular) and 700 (bold, for `<strong>`) — so every weight a block asks for has a
 * real face. Returns null when both roles are system/generic families (nothing to load).
 */
export function themeFontLinkHref(theme: ThemeSettings | undefined): string | null {
  const ty = theme?.typography;
  if (!ty) return null;
  const byFamily = new Map<string, Set<number>>();
  for (const role of [ty.heading, ty.body]) {
    const fam = primaryFamily(role?.font);
    if (!fam) continue;
    const w = Number(role?.weight);
    const weights = byFamily.get(fam) ?? new Set<number>();
    weights.add(400);
    weights.add(700);
    if (Number.isFinite(w) && w >= 100 && w <= 900) weights.add(w);
    byFamily.set(fam, weights);
  }
  if (byFamily.size === 0) return null;
  const families = [...byFamily.entries()].map(([fam, weights]) => {
    const wl = [...weights].sort((a, b) => a - b).join(';');
    return `family=${fam.replace(/ /g, '+')}:wght@${wl}`;
  });
  return `https://fonts.googleapis.com/css2?${families.join('&')}&display=swap`;
}

const LINK_ID = 'bld-theme-fonts';

/** Upsert the `<link rel=stylesheet>` that loads the theme webfont into the builder document head. SSR-safe. */
export function syncThemeFontLink(theme: ThemeSettings | undefined): void {
  if (typeof document === 'undefined') return;
  const href = themeFontLinkHref(theme);
  let link = document.getElementById(LINK_ID) as HTMLLinkElement | null;
  if (!href) {
    link?.remove();
    return;
  }
  if (!link) {
    link = document.createElement('link');
    link.id = LINK_ID;
    link.rel = 'stylesheet';
    document.head.appendChild(link);
  }
  if (link.href !== href) link.href = href;
}
